import { z } from "zod";
import { isIpCidr } from "./ip.js";
import { Labels } from "./ssh-keys.js";

export const FirewallName = z.string().trim().min(1).max(128);

export const FirewallDirection = z.enum(["in", "out"]);
export type FirewallDirection = z.infer<typeof FirewallDirection>;

export const FirewallProtocol = z.enum(["tcp", "udp", "icmp", "esp", "gre"]);
export type FirewallProtocol = z.infer<typeof FirewallProtocol>;

/** Protocols that have ports; the others match whole packets. */
export const PORT_PROTOCOLS: readonly FirewallProtocol[] = ["tcp", "udp"];

/** The whole IPv4 and IPv6 internet, the "anywhere" shortcuts of the rule editor. */
export const ANYWHERE_V4 = "0.0.0.0/0";
export const ANYWHERE_V6 = "::/0";

/**
 * Every port of a protocol. Hetzner documents ports and ranges only (not
 * "any" or an omitted port), so "any port" is sent as the full range.
 */
export const ALL_PORTS = "1-65535";

/** True for the full port range, however it is written (`1-65535`, `any`). */
export const isAllPorts = (port: string | null): boolean =>
  port !== null && (port.trim() === ALL_PORTS || port.trim().toLowerCase() === "any");

/** Most rules one firewall may hold (Hetzner's limit, assumed the same elsewhere). */
export const MAX_FIREWALL_RULES = 50;

/** `22` or `8000-8100`, each port 1–65535, the range ascending. */
export function isPortSpec(value: string): boolean {
  const match = /^(\d{1,5})(?:-(\d{1,5}))?$/.exec(value.trim());
  if (!match) return false;
  const from = Number(match[1]);
  const to = match[2] === undefined ? from : Number(match[2]);
  return from >= 1 && to <= 65_535 && from <= to;
}

const Cidrs = z.array(
  z
    .string()
    .trim()
    .refine(isIpCidr, {
      message: "Not an address with a prefix, e.g. 203.0.113.0/24 or 2001:db8::/32",
      params: { reason: "ip_cidr_invalid" },
    }),
);

/**
 * One rule. Rules only allow traffic: once a firewall is applied, everything
 * no rule matches is dropped. Inbound rules name where traffic may come from
 * (`sourceIps`), outbound rules where it may go (`destinationIps`).
 */
export const FirewallRule = z
  .object({
    direction: FirewallDirection,
    protocol: FirewallProtocol,
    /** Port or range for tcp/udp, null for the other protocols. */
    port: z.string().trim().nullable(),
    sourceIps: Cidrs.default([]),
    destinationIps: Cidrs.default([]),
    description: z.string().trim().max(255).nullable().default(null),
  })
  .superRefine((rule, ctx) => {
    const hasPorts = PORT_PROTOCOLS.includes(rule.protocol);
    if (hasPorts && (rule.port === null || !isPortSpec(rule.port))) {
      ctx.addIssue({
        code: "custom",
        path: ["port"],
        message: "Give a port (22) or a range (8000-8100)",
      });
    }
    if (!hasPorts && rule.port !== null) {
      ctx.addIssue({ code: "custom", path: ["port"], message: "Only tcp and udp have ports" });
    }
    const own = rule.direction === "in" ? "sourceIps" : "destinationIps";
    const other = rule.direction === "in" ? "destinationIps" : "sourceIps";
    if (rule[own].length === 0) {
      ctx.addIssue({ code: "custom", path: [own], message: "Name at least one address range" });
    }
    if (rule[other].length > 0) {
      ctx.addIssue({
        code: "custom",
        path: [other],
        message:
          rule.direction === "in"
            ? "Inbound rules have sources only"
            : "Outbound rules have destinations only",
      });
    }
  });
export type FirewallRule = z.output<typeof FirewallRule>;
export type FirewallRuleInput = z.input<typeof FirewallRule>;

export const FirewallRules = z.array(FirewallRule).max(MAX_FIREWALL_RULES);

/** A label selector such as `role=web` or `env in (prod,staging)`; the provider checks the syntax. */
export const LabelSelector = z.string().trim().min(1).max(512);

/** Where a firewall is applied, as the adapter reports it (provider ids of servers). */
export const FirewallTargetData = z.discriminatedUnion("type", [
  z.object({ type: z.literal("server"), serverRef: z.string() }),
  z.object({
    type: z.literal("label_selector"),
    selector: z.string(),
    /** Servers the selector matches right now. */
    serverRefs: z.array(z.string()),
  }),
]);
export type FirewallTargetData = z.infer<typeof FirewallTargetData>;

/** A firewall as the provider adapter reports it (without our id). */
export const FirewallData = z.object({
  name: z.string(),
  rules: z.array(FirewallRule),
  appliedTo: z.array(FirewallTargetData),
  labels: Labels,
  createdAt: z.iso.datetime(),
});
export type FirewallData = z.infer<typeof FirewallData>;

export const FirewallServer = z.object({ id: z.uuid(), name: z.string() });
export type FirewallServer = z.infer<typeof FirewallServer>;

export const Firewall = FirewallData.omit({ appliedTo: true }).extend({
  id: z.uuid(),
  appliedTo: z.object({
    /** Servers the firewall is applied to directly. */
    servers: z.array(FirewallServer),
    labelSelectors: z.array(z.object({ selector: z.string(), servers: z.array(FirewallServer) })),
  }),
});
export type Firewall = z.infer<typeof Firewall>;

/** Servers (our ids) and label selectors a firewall is applied to or removed from. */
export const FirewallTargets = z
  .object({
    serverIds: z.array(z.uuid()).max(100).default([]),
    labelSelectors: z.array(LabelSelector).max(20).default([]),
  })
  .refine(
    (v) => v.serverIds.length + v.labelSelectors.length > 0,
    "Choose at least one server or label selector",
  );
export type FirewallTargets = z.input<typeof FirewallTargets>;

export const CreateFirewallRequest = z.object({
  name: FirewallName,
  rules: FirewallRules.default([]),
  serverIds: z.array(z.uuid()).max(100).default([]),
  labelSelectors: z.array(LabelSelector).max(20).default([]),
  labels: Labels.default({}),
});
export type CreateFirewallRequest = z.input<typeof CreateFirewallRequest>;

export const UpdateFirewallRequest = z
  .object({ name: FirewallName.optional(), labels: Labels.optional() })
  .refine((v) => v.name !== undefined || v.labels !== undefined, "Nothing to change");
export type UpdateFirewallRequest = z.infer<typeof UpdateFirewallRequest>;

export const SetFirewallRulesRequest = z.object({ rules: FirewallRules });
export type SetFirewallRulesRequest = z.input<typeof SetFirewallRulesRequest>;
